/**
 * Advanced Beer-Food Pairing Algorithm
 * Based on "The Cicerone's Cipher v2" - Multi-Course Meal & Progressive Pairing Engine
 */

// --- DATA MOCKS v6 (With new contextual data) ---
const menuItemsData = [
  {
    id: 1,
    name: "Grilled Steak",
    profile: {
      fat: 8,
      acidity: 1,
      sweetness: 2,
      bitterness: 4,
      umami: 8,
      salt: 5,
      spice: 1,
      intensity: 9,
      texture: "chewy",
      temp: "hot",
      cookingMethod: "grilling",
      aromaCompounds: ["maillard"],
      problemIngredients: [],
      cuisine: "American",
    },
  },
  {
    id: 2,
    name: "Steamed Artichoke",
    profile: {
      fat: 4,
      acidity: 2,
      sweetness: 1,
      bitterness: 6,
      umami: 3,
      salt: 2,
      spice: 0,
      intensity: 4,
      texture: "soft",
      temp: "hot",
      cookingMethod: "steaming",
      aromaCompounds: ["vegetal"],
      problemIngredients: ["cynarin"],
      cuisine: "French",
    },
  },
  {
    id: 3,
    name: "Fish & Chips",
    profile: {
      fat: 8,
      acidity: 4,
      sweetness: 1,
      bitterness: 2,
      umami: 4,
      salt: 7,
      spice: 0,
      intensity: 6,
      texture: "crispy",
      temp: "hot",
      cookingMethod: "frying",
      aromaCompounds: ["maillard"],
      problemIngredients: [],
      cuisine: "British",
    },
  },
  {
    id: 4,
    name: "Spicy Thai Green Curry",
    profile: {
      fat: 7,
      acidity: 4,
      sweetness: 3,
      bitterness: 2,
      umami: 7,
      salt: 4,
      spice: 8,
      intensity: 8,
      texture: "creamy",
      temp: "hot",
      cookingMethod: "stewing",
      aromaCompounds: ["linalool", "gingerol"],
      problemIngredients: [],
      cuisine: "Thai",
    },
  },
  {
    id: 5,
    name: "Weisswurst & Pretzel",
    profile: {
      fat: 6,
      acidity: 1,
      sweetness: 2,
      bitterness: 1,
      umami: 6,
      salt: 7,
      spice: 2,
      intensity: 6,
      texture: "soft",
      temp: "hot",
      cookingMethod: "boiling",
      aromaCompounds: ["eugenol", "maillard_light"],
      problemIngredients: [],
      cuisine: "German",
    },
  },
  {
    id: 6,
    name: "Oysters on the Half Shell",
    profile: {
      fat: 2,
      acidity: 2,
      sweetness: 1,
      bitterness: 1,
      umami: 7,
      salt: 9,
      spice: 0,
      intensity: 4,
      texture: "slimy",
      temp: "chilled",
      cookingMethod: "raw",
      aromaCompounds: ["saline"],
      problemIngredients: [],
      cuisine: "Global",
    },
  },
];

const beersData = [
  {
    id: 1,
    name: "American IPA",
    profile: {
      ibu: 65,
      abv: 6.8,
      sweetness: 3,
      acidity: 5,
      carbonation: 6,
      body: 6,
      intensity: 8,
      maillard: 4,
      caramel: 2,
      fruityEster: 7,
      spicyPhenol: 2,
      idealTemp: 7,
      color: "gold",
      region: "American",
    },
  },
  {
    id: 2,
    name: "German Hefeweizen",
    profile: {
      ibu: 12,
      abv: 5.2,
      sweetness: 4,
      acidity: 6,
      carbonation: 8,
      body: 5,
      intensity: 5,
      maillard: 3,
      caramel: 1,
      fruityEster: 9,
      spicyPhenol: 8,
      idealTemp: 6,
      color: "straw",
      region: "German",
    },
  },
  {
    id: 3,
    name: "Irish Stout (Nitro)",
    profile: {
      ibu: 40,
      abv: 4.5,
      sweetness: 4,
      acidity: 4,
      carbonation: 2,
      body: 7,
      intensity: 7,
      maillard: 9,
      caramel: 2,
      fruityEster: 3,
      spicyPhenol: 1,
      idealTemp: 8,
      color: "black",
      region: "Irish",
    },
  },
  {
    id: 4,
    name: "Belgian Dubbel",
    profile: {
      ibu: 25,
      abv: 7.5,
      sweetness: 6,
      acidity: 4,
      carbonation: 7,
      body: 7,
      intensity: 8,
      maillard: 6,
      caramel: 7,
      fruityEster: 7,
      spicyPhenol: 6,
      idealTemp: 10,
      color: "amber",
      region: "Belgian",
    },
  },
  {
    id: 5,
    name: "German Pilsner",
    profile: {
      ibu: 35,
      abv: 4.8,
      sweetness: 2,
      acidity: 5,
      carbonation: 8,
      body: 4,
      intensity: 4,
      maillard: 2,
      caramel: 0,
      fruityEster: 2,
      spicyPhenol: 1,
      idealTemp: 5,
      color: "straw",
      region: "German",
    },
  },
  {
    id: 6,
    name: "English Bitter",
    profile: {
      ibu: 30,
      abv: 4.0,
      sweetness: 4,
      acidity: 3,
      carbonation: 4,
      body: 5,
      intensity: 5,
      maillard: 5,
      caramel: 4,
      fruityEster: 6,
      spicyPhenol: 1,
      idealTemp: 12,
      color: "copper",
      region: "British",
    },
  },
];

/**
 * Advanced Beer Pairing Algorithm v6
 * Calculates pairing scores based on multiple factors including taste interaction,
 * contextual harmony, visual aesthetics, and progressive meal dynamics
 */
export const getAdvancedBeerPairingScore = (food, beer, previousBeers = []) => {
  let score = 0;
  const rationale = [];
  if (!food?.profile || !beer?.profile) return { score: 0, rationale: [] };

  const foodProfile = food.profile;
  const beerProfile = beer.profile;

  // --- Layer 1: Foundational Filters (Intensity Match) ---
  if (Math.abs(foodProfile.intensity - beerProfile.intensity) <= 2) {
    score += 5;
    rationale.push({
      p: "+5",
      r: "Intensity Match: Beer and dish are well-balanced in power.",
    });
  } else if (Math.abs(foodProfile.intensity - beerProfile.intensity) > 4) {
    score -= 10;
    rationale.push({
      p: "-10",
      r: "Intensity Mismatch: One will overwhelm the other.",
    });
  }

  // --- Layer 2: Core Taste Interaction (Rules Engine) ---
  if (foodProfile.fat > 6) {
    const carbonationBonus = beerProfile.carbonation * 1.5;
    score += carbonationBonus;
    rationale.push({
      p: `+${carbonationBonus.toFixed(0)}`,
      r: "Cut: Carbonation scrubs palate from fat.",
    });

    const ibuBonus = beerProfile.ibu * 0.1;
    score += ibuBonus;
    rationale.push({
      p: `+${ibuBonus.toFixed(1)}`,
      r: "Cut: Bitterness contrasts richness.",
    });
  }

  if (foodProfile.spice > 5) {
    const sweetnessBonus = beerProfile.sweetness * 1.5;
    score += sweetnessBonus;
    rationale.push({
      p: `+${sweetnessBonus.toFixed(0)}`,
      r: "Balance: Malt sweetness cools spice.",
    });
  }

  if (foodProfile.maillard > 5 && beerProfile.maillard > 5) {
    const maillardBonus = (foodProfile.maillard + beerProfile.maillard) * 0.5;
    score += maillardBonus;
    rationale.push({
      p: `+${maillardBonus.toFixed(0)}`,
      r: "Complement: Shared Maillard flavors.",
    });
  }

  if (
    foodProfile.spice > 6 &&
    (beerProfile.abv > 7.0 || beerProfile.ibu > 50)
  ) {
    score -= 15;
    rationale.push({
      p: "-15",
      r: "CLASH: High ABV/IBU amplifies spice heat.",
    });
  }

  if (foodProfile.umami > 7 && beerProfile.ibu > 40) {
    score -= 15;
    rationale.push({ p: "-15", r: "CLASH: Umami makes high-IBU beer harsh." });
  }

  // --- NEW v6: Advanced Contextual & Progressive Pairing Logic ---
  // 1. Contextual "Terroir" Bonus
  if (
    food.profile.cuisine &&
    beer.profile.region &&
    food.profile.cuisine === beer.profile.region
  ) {
    score += 5;
    rationale.push({
      p: "+5",
      r: "Terroir Bonus: Classic regional pairing ('What grows together, goes together').",
    });
  }

  // 2. Visual & Aesthetic Pairing
  const colorHarmony = {
    dark: ["black", "amber"],
    light: ["gold", "straw", "copper"],
  };
  const foodVisual = food.profile.intensity > 6 ? "dark" : "light";
  const beerVisual = beer.profile.color;
  if (
    (foodVisual === "dark" && colorHarmony.dark.includes(beerVisual)) ||
    (foodVisual === "light" && colorHarmony.light.includes(beerVisual))
  ) {
    score += 2;
    rationale.push({
      p: "+2",
      r: "Visual Harmony: The color of the beer complements the dish's presentation.",
    });
  }

  // 3. Dynamic Palate Fatigue Modeling
  if (previousBeers.length > 0) {
    const lastBeer = previousBeers[previousBeers.length - 1];
    const intensityDrop = lastBeer.profile.intensity - beerProfile.intensity;
    const ibuDrop = lastBeer.profile.ibu - beerProfile.ibu;

    if (intensityDrop > 3) {
      const penalty = intensityDrop * 1.5;
      score -= penalty;
      rationale.push({
        p: `-${penalty.toFixed(0)}`,
        r: `Palate Fatigue: This beer is much less intense than the previous one; its flavor may be lost.`,
      });
    }
    if (ibuDrop > 30) {
      score -= 5;
      rationale.push({
        p: "-5",
        r: "IBU Shock: Sharp drop in bitterness can make this beer seem watery.",
      });
    }
    if (beerProfile.intensity > lastBeer.profile.intensity) {
      score += 2;
      rationale.push({
        p: "+2",
        r: "Good Progression: Intensity builds logically from previous course.",
      });
    }
  }

  // --- Final Interpretation ---
  let interpretation = "",
    colorClass = "";
  if (score > 15) {
    interpretation = "Excellent Pairing";
    colorClass = "text-green-400";
  } else if (score >= 5) {
    interpretation = "Good Pairing";
    colorClass = "text-blue-400";
  } else if (score > -5) {
    interpretation = "Neutral / Risky";
    colorClass = "text-yellow-400";
  } else {
    interpretation = "Problematic Pairing";
    colorClass = "text-red-400";
  }

  return { score, interpretation, rationale, colorClass };
};

/**
 * Advanced Beer Pairing Service Class
 * Provides methods for beer-food pairing analysis and recommendations
 */
export class AdvancedBeerPairingService {
  constructor() {
    this.menuItems = menuItemsData;
    this.beers = beersData;
  }

  /**
   * Get pairing score for a specific beer-food combination
   */
  getPairingScore(foodId, beerId, previousBeers = []) {
    const food = this.menuItems.find((f) => f.id === foodId);
    const beer = this.beers.find((b) => b.id === beerId);

    if (!food || !beer) {
      return { score: 0, rationale: [], error: "Food or beer not found" };
    }

    return getAdvancedBeerPairingScore(food, beer, previousBeers);
  }

  /**
   * Get best beer recommendations for a given food item
   */
  getBeerRecommendations(foodId, previousBeers = [], limit = 3) {
    const food = this.menuItems.find((f) => f.id === foodId);
    if (!food) return [];

    const recommendations = this.beers.map((beer) => {
      const pairingResult = getAdvancedBeerPairingScore(
        food,
        beer,
        previousBeers
      );
      return {
        beer,
        ...pairingResult,
      };
    });

    return recommendations.sort((a, b) => b.score - a.score).slice(0, limit);
  }

  /**
   * Analyze a complete meal progression
   */
  analyzeMealProgression(courses) {
    const previousBeers = [];
    const results = courses.map((course, index) => {
      const food = this.menuItems.find((f) => f.id === course.foodId);
      const beer = this.beers.find((b) => b.id === course.beerId);

      if (!food || !beer) {
        return { error: "Food or beer not found", courseIndex: index };
      }

      const result = getAdvancedBeerPairingScore(food, beer, previousBeers);
      previousBeers.push(beer);

      return {
        food,
        beer,
        courseIndex: index,
        ...result,
      };
    });

    const totalScore =
      results.length > 0
        ? results.reduce((sum, result) => sum + (result.score || 0), 0) /
          results.length
        : 0;

    return {
      results,
      totalScore,
      overallHarmony: this.getOverallHarmonyRating(totalScore),
    };
  }

  /**
   * Get overall harmony rating based on total score
   */
  getOverallHarmonyRating(totalScore) {
    if (totalScore > 15)
      return { rating: "Exceptional", color: "text-green-400" };
    if (totalScore > 10)
      return { rating: "Excellent", color: "text-green-300" };
    if (totalScore > 5) return { rating: "Good", color: "text-blue-400" };
    if (totalScore > 0) return { rating: "Fair", color: "text-yellow-400" };
    return { rating: "Needs Work", color: "text-red-400" };
  }

  /**
   * Get available menu items
   */
  getMenuItems() {
    return this.menuItems;
  }

  /**
   * Get available beers
   */
  getBeers() {
    return this.beers;
  }

  /**
   * Add custom food item
   */
  addCustomFood(foodData) {
    const newFood = {
      id: Math.max(...this.menuItems.map((f) => f.id)) + 1,
      ...foodData,
    };
    this.menuItems.push(newFood);
    return newFood;
  }

  /**
   * Add custom beer
   */
  addCustomBeer(beerData) {
    const newBeer = {
      id: Math.max(...this.beers.map((b) => b.id)) + 1,
      ...beerData,
    };
    this.beers.push(newBeer);
    return newBeer;
  }
}

// Create singleton instance
export const advancedBeerPairingService = new AdvancedBeerPairingService();

// Default export
export default advancedBeerPairingService;
